import { useState } from "react";

import {
	Button,
	Dialog,
	DialogContent,
	Grid2,
	IconButton,
	Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { FaXmark } from "react-icons/fa6";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { apis, callApi } from "../../../api";
import { AccountService } from "../../../hooks/useAccount";
import useScreenSize from "../../../hooks/useScreenSize";
import { StripeService } from "../../../hooks/useStripe";
import { TokenService } from "../../../hooks/useToken";
import { useWhiteScreen } from "../../../hooks/useWhiteScreen";
import { rules } from "../../../utils";
import { CommonInput } from "../../common";
import { Container } from "../../common/Container/Container";

import "./login.scss";

export type TLoginFieldValues = {
	email: string;
	password: string;
};

export type TRecoveryPasswordFieldValues = {
	emailRecovery: string;
};

export function Login() {
	useWhiteScreen();
	const [isForgotPasswordModalOpen, setIsShowForgotPasswordModalOpen] =
		useState(false);
	const [searchParams] = useSearchParams();
	const { width } = useScreenSize();
	const isMobile = width <= 768;

	const { handleSubmit: handleSubmitSignIn, control: controlSignIn } =
		useForm<TLoginFieldValues>({
			defaultValues: {
				email: "",
				password: "",
			},
			mode: "all",
		});

	const {
		handleSubmit: handleSubmitForgotPassword,
		control: controlForgotPassword,
		setValue: setValueForgotPassword,
	} = useForm<TRecoveryPasswordFieldValues>({
		defaultValues: {
			emailRecovery: "",
		},
		mode: "all",
	});

	const navigate = useNavigate();

	const openForgotPasswordModal = () => {
		setIsShowForgotPasswordModalOpen(true);
	};

	const closeForgotPasswordModal = () => {
		setIsShowForgotPasswordModalOpen(false);
	};

	const onClickLogin = async (data: TLoginFieldValues) => {
		try {
			const { email, password } = data;
			const response = await callApi({
				method: "POST",
				url: apis.SIGN_IN,
				body: { email, password },
			});

			const { user, token, stripeInfo } = response;
			const redirect = searchParams.get("redirect");

			if (redirect) {
				TokenService.saveToken(token);
				AccountService.saveAccount(user);
				if (stripeInfo) {
					StripeService.saveStripeData(stripeInfo);
				}
				navigate(redirect.replace(/_/g, "#"), { replace: true });
				return;
			}

			TokenService.saveToken(token);
			AccountService.saveAccount(user);
			if (stripeInfo) {
				StripeService.saveStripeData(stripeInfo);
			}
			navigate("/training/dashboard");
		} catch (error: any) {
			if (error?.response?.status === 400) {
				alert("Wrong email or password, please check your information again!");
			}
		}
	};

	const onClickRequestRecoveryPassword = async (
		data: TRecoveryPasswordFieldValues,
	) => {
		try {
			const { emailRecovery } = data;

			await callApi({
				method: "POST",
				url: apis.RECOVER_PASSWORD,
				body: { emailRecovery },
			});

			closeForgotPasswordModal();
			toast.success(
				"An email has been sent to you, please check your email and continue to reset your password!",
			);
			setValueForgotPassword("emailRecovery", "");
		} catch (error) {
			alert("Wrong email, please check your information again!");
		}
	};

	const redirect = searchParams.get("redirect");
	let signUpLink = "/sign-up";
	if (redirect) {
		signUpLink = `/sign-up?redirect=${redirect}`;
	}

	return (
		<Container
			noFooter
			className="login-container"
			navbarOrkaProps={{ white: true }}>
			<Grid2 container>
				<Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
					<form
						className="login-form"
						onSubmit={handleSubmitSignIn(onClickLogin)}>
						<Typography className="login-text">SIGN IN</Typography>
						<CommonInput
							control={controlSignIn}
							name="email"
							type="email"
							placeholder="Email"
							rules={{
								required: {
									value: true,
									message: "Email is required.",
								},
								pattern: rules.email.pattern,
							}}
							className="sign-in-input"
						/>
						<CommonInput
							control={controlSignIn}
							name="password"
							type="password"
							placeholder="Password"
							rules={{
								required: {
									value: true,
									message: "Password is required.",
								},
							}}
							className="sign-in-input"
						/>

						<Typography
							className="normal-text forgot-password-text"
							onClick={openForgotPasswordModal}>
							Forgot your Password?
						</Typography>

						<Button
							className="normal-button primary-button login-button"
							fullWidth
							type="submit">
							Sign In
						</Button>
						<Typography className="normal-text sign-up-text">
							Don't have an account?{" "}
							<Link to={signUpLink} className="normal-text sign-up-button">
								Sign Up Here
							</Link>
						</Typography>
					</form>
				</Grid2>

				{!isMobile && (
					<Grid2 size={{ md: 8, lg: 8, xl: 8 }}>
						<div style={{ marginLeft: "5vw", width: "100%", height: "100%" }}>
							<img
								src={require("../../imagenes/botones2.png")}
								alt="botones2"
								style={{
									width: "100%",
									height: "auto",
									minHeight: "100%",
									objectFit: "cover",
								}}
							/>
						</div>
					</Grid2>
				)}
			</Grid2>
			<Dialog
				open={isForgotPasswordModalOpen}
				onClose={closeForgotPasswordModal}
				className="modal-login-forgot-password">
				<DialogContent>
					<div className="dialog-header">
						<IconButton onClick={closeForgotPasswordModal}>
							<FaXmark className="normal-icon close-icon" />
						</IconButton>
					</div>

					<form
						className="forgot-password-form"
						onSubmit={handleSubmitForgotPassword(
							onClickRequestRecoveryPassword,
						)}>
						<Typography className="account-recovery">
							Account Recovery
						</Typography>
						<Typography className="normal-text enter-email-text">
							Enter the email you use for ORKA, and we'll help you recover your
							password.
						</Typography>
						<CommonInput
							control={controlForgotPassword}
							name="emailRecovery"
							type="email"
							placeholder="Email"
							rules={{
								required: {
									value: true,
									message: "Email is required.",
								},
								pattern: rules.email.pattern,
							}}
						/>
						<Button
							className="normal-button primary-button submit-email-recovery-button"
							type="submit">
							Next
						</Button>
					</form>
				</DialogContent>
			</Dialog>
		</Container>
	);
}
